import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { createSelector } from 'reselect';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { getAvailableShippingOptions } from '../../store/csw/csw.api';
import { filterDutiesOptions } from '../duties-and-taxes/helpers';

import Global from '../../store/global-selectors';
import { addressPropType } from '../../address/address-fields/address-prop-type';
import { InsuranceProviders } from '../../enums/insurance-providers';
import { RATE_BROWSER, CUSTOMERS_CREATE_LABEL } from '../../constants/fulfillment-plan-ids';
import {
  CUSTOMERS_CREATE_LABEL as CUSTOMERS_CREATE_LABEL_TYPE,
  LOCAL_DELIVERY_RATE,
  RATE_BROWSER as RATE_BROWSER_TYPE,
} from '../../enums/csw-types';
import * as Actions from '../../store/csw/csw.actions';
import { useIsServiceLocalDelivery } from '../../hooks/use-is-service-local-delivery';
import { useGetValidConfirmationForService } from '../hooks/use-get-valid-confirmation-for-service';
import { useCSWTypeId } from '../hooks/use-csw-type-id';
import { useNeedsCustomsState } from '../hooks/use-needs-customs-state';
import { isAddressCompleteForRating, useShipToState } from '../hooks/use-ship-to-state';
import { useIsReturnState } from '../hooks/use-is-return-state';

import { CustomsService } from '../../services/customs-service';

import { FulfillmentPlannerContextProvider } from '../context';
import { getShipFromIdOrDefault } from '../helpers/get-ship-from';
import { Recipient } from '../duties-and-taxes/constants';

const RATE_VIEWER_RATE_BROWSER_KEY = 'rateBrowser';

const getNewCswTypeId = (fulfillmentPlanId, _, __, isLocalDelivery) => {
  if (isLocalDelivery) {
    return LOCAL_DELIVERY_RATE;
  }
  if (fulfillmentPlanId === RATE_BROWSER) {
    return RATE_BROWSER_TYPE;
  }
  return CUSTOMERS_CREATE_LABEL_TYPE;
};

const availableSellerShippingServicesSelector = () =>
  createSelector(
    (stringifiedServices) => stringifiedServices,
    (stringifiedServices) => {
      return JSON.parse(stringifiedServices);
    },
  );

const useShowDutiesAndTaxes = ({
  availableSellerShippingServices,
  serviceIdAndSellerLabelProviderId,
  shipFromId,
  shipTo,
  onShippingOptionsChange,
}) => {
  const [servicesIDsWithDuties, setHasServiceIdsWithDuties] = useAsyncSafeState(new Set());
  const selectedServiceId = serviceIdAndSellerLabelProviderId.serviceId;
  const shouldShowCustom = servicesIDsWithDuties.has(selectedServiceId);

  const availableSellerShippingServicesSelectorInstance = useMemo(availableSellerShippingServicesSelector, []);
  const safeShippingServices = availableSellerShippingServicesSelectorInstance(JSON.stringify(availableSellerShippingServices || []));
  useEffect(() => {
    if (safeShippingServices.length) {
      getAvailableShippingOptions({
        shipTo: {
          city: shipTo?.city,
          state: shipTo?.state,
          countryCode: shipTo?.countryCode,
        },
        shipFromId,
        serviceIds: safeShippingServices,
      })
        .then(({ data }) => {
          const newSetServicesIDsWithDuties = new Set(Object.keys(data || {}));
          const [options] = Object.values(data || {});
          if (options && newSetServicesIDsWithDuties.has(selectedServiceId)) {
            onShippingOptionsChange(data[selectedServiceId] || []);
          } else {
            onShippingOptionsChange([]);
          }
          setHasServiceIdsWithDuties(newSetServicesIDsWithDuties);
        })
        .catch(() => {
          // Not doing anything in here for now. Need to work with UX on what to display.
        });
    }
  }, [
    safeShippingServices,
    selectedServiceId,
    onShippingOptionsChange,
    setHasServiceIdsWithDuties,
    shipFromId,
    shipTo?.city,
    shipTo?.state,
    shipTo?.countryCode,
  ]);

  return shouldShowCustom;
};

export const RateBrowserAdapterForFulfillmentPlannerContext = React.memo(
  ({ children, fulfillmentPlanId, initialEmailAddress = '', initialShipTo }) => {
    const initialShipFromId = useSelector((state) => {
      const rateBrowserShipFromId = Global.selectRateBrowsergetConfigureRatesModel(state)?.shipFromId;
      const shipFroms = Global.selectSellerShipFroms(state);
      return getShipFromIdOrDefault(rateBrowserShipFromId, shipFroms);
    });
    const weight = useSelector((state) => {
      return Global.selectRateBrowsergetConfigureRatesModel(state)?.weight;
    });
    const dimensions = useSelector((state) => {
      return Global.selectRateBrowsergetConfigureRatesModel(state)?.dimensions;
    });
    const confirmationType = useSelector((state) => {
      return Global.selectRateBrowsergetConfigureRatesModel(state)?.confirmationType;
    });

    const rateBrowserServiceId = useSelector((state) => {
      return Global.selectRateViewerRate(state, {
        rateViewerKey: RATE_VIEWER_RATE_BROWSER_KEY,
      })?.serviceId;
    });

    const selectedRatePackageTypeId = useSelector((state) => {
      return Global.selectRateViewerRate(state, {
        rateViewerKey: RATE_VIEWER_RATE_BROWSER_KEY,
      })?.packageTypeId;
    });
    const selectedRatePackageId = useSelector((state) => {
      return Global.selectRateViewerRate(state, {
        rateViewerKey: RATE_VIEWER_RATE_BROWSER_KEY,
      })?.packageId;
    });

    const rateBrowserServiceCollectionGuid = useSelector((state) => {
      return Global.selectRateViewerRate(state, {
        rateViewerKey: RATE_VIEWER_RATE_BROWSER_KEY,
      })?.serviceCollectionGuid;
    });

    const initialPackages = useMemo(() => {
      return [
        {
          packageTypeId: selectedRatePackageTypeId,
          packageId: selectedRatePackageId,
          weight: {
            value: weight?.weight,
            unit: weight?.weightUnit,
          },
          dimensions,
          insuredValue: null,
        },
      ];
    }, [selectedRatePackageTypeId, selectedRatePackageId, weight, dimensions]);

    const rateBrowserSellerLabelProviderId = useSelector((state) => {
      return Global.selectRateViewerSellerProviderId(state, {
        rateViewerKey: RATE_VIEWER_RATE_BROWSER_KEY,
      });
    });

    const isLocalDelivery = useIsServiceLocalDelivery(rateBrowserServiceId);
    const initialCSWTypeId = useMemo(() => {
      return getNewCswTypeId(fulfillmentPlanId, undefined, undefined, isLocalDelivery);
      // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [isReturn, setIsReturn] = useState(false);

    const [shipTo, setShipTo] = useState(initialShipTo);
    const [emailAddress, setEmailAddress] = useState(initialEmailAddress ?? '');
    const [shipFromId, setShipFromId] = useState(initialShipFromId);

    const [packages, setPackages] = useState(initialPackages);
    const [needsCustoms, setNeedsCustoms] = useState(false);
    const [shipDate, setShipDate] = useState(null);
    const [insuranceProvider, setInsuranceProvider] = useState(InsuranceProviders.None);

    const getValidConfirmationForService = useGetValidConfirmationForService();
    const [otherShippingOptions, setOtherShippingOptions] = useState(() => {
      const validatedConfirmationId = getValidConfirmationForService(rateBrowserServiceId, confirmationType);
      return { confirmationEnumId: validatedConfirmationId };
    });
    const [serviceIdAndSellerLabelProviderId, setServiceIdAndSellerLabelProviderId] = useState({
      serviceId: rateBrowserServiceId,
      sellerLabelProviderId: rateBrowserSellerLabelProviderId,
    });
    const [serviceCollectionGuid, setServiceCollectionGuid] = useState(rateBrowserServiceCollectionGuid ?? null);

    const sellerCurrencyCode = useSelector(Global.selectSellerDefaultCurrency);
    const [driverTip, setDriverTip] = useState({
      value: 0,
      code: sellerCurrencyCode,
    });
    const [pickup, setPickup] = useState(null);

    const defaultCountryCode = useSelector(Global.selectSellerHomeCountry);
    const defaultWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit);
    const defaultCurrencyCode = useSelector(Global.selectSellerDefaultCurrency) || 'USD';
    const defaultCustomsValues = useSelector(Global.selectSellerDefaultCustomData);
    const [customs, setCustoms] = useState(() => {
      const initialValidatedCustoms = CustomsService.validateCustoms({}, defaultCountryCode, defaultCurrencyCode, defaultWeightUnit);
      return CustomsService.fillCustomsWithDefaults(initialValidatedCustoms, defaultCustomsValues, defaultCurrencyCode, defaultWeightUnit);
    });
    const [disableTriggerRate, setDisableTriggerRate] = useState(true);

    const { updateCSWTypeId } = useCSWTypeId(fulfillmentPlanId, initialCSWTypeId, getNewCswTypeId);
    const { updateNeedsCustoms } = useNeedsCustomsState(fulfillmentPlanId);
    useEffect(() => {
      updateNeedsCustoms(shipFromId, shipTo, rateBrowserSellerLabelProviderId, rateBrowserServiceId, null, false).then(
        (newNeedsCustoms) => {
          setNeedsCustoms(newNeedsCustoms);
        },
      );
    }, []); // oxlint-disable-line react-hooks/exhaustive-deps
    // we only need to run the above on mount to get an initial value

    const dispatch = useDispatch();

    const [availableSellerShippingServices, setAvailableSellerShippingServices] = useState([]);

    const [availableShippingOptions, setAvailableShippingOptions] = useState([]);

    const onShippingOptionsChange = useCallback(
      (newShippingOptions) => {
        setAvailableShippingOptions(newShippingOptions);
        dispatch(
          Actions.updateShipmentConfiguration(fulfillmentPlanId, {
            availableShippingOptions: newShippingOptions,
          }),
        );

        setOtherShippingOptions((prev) => {
          if (!prev || prev.additionalOptions) {
            return prev;
          }
          const recipientOption = newShippingOptions.find((opt) => opt?.additionalOptionValue?.prepaidDuty === Recipient);
          if (recipientOption) {
            return { ...prev, additionalOptions: [recipientOption] };
          }
          return prev;
        });
      },
      [dispatch, fulfillmentPlanId],
    );

    const availableDutiesOptions = useMemo(() => {
      return filterDutiesOptions(availableShippingOptions);
    }, [availableShippingOptions]);

    const shouldShowDutiesAndTaxes = useShowDutiesAndTaxes({
      availableSellerShippingServices,
      serviceIdAndSellerLabelProviderId,
      shipFromId,
      shipTo,
      onShippingOptionsChange,
    });
    const onConfigurationChange = useCallback(
      (payload, options = {}) => {
        const { shouldUpdateNeedsCustoms, shouldSkipRateRequest } = options;
        if (payload?.packages) {
          setPackages(payload.packages);
        }
        // check !== undefined for any value which may be "unset" (null) by the user or by the system to allow the null value
        if (payload?.shipFromId !== undefined) {
          setShipFromId(payload.shipFromId);
        }
        if (payload?.shipDate !== undefined) {
          setShipDate(payload.shipDate);
        }
        if (payload?.insuranceProvider) {
          setInsuranceProvider(InsuranceProviders[payload.insuranceProvider]);
        }
        if (payload?.options) {
          setOtherShippingOptions(payload.options);
        }
        if (
          payload?.serviceId !== undefined ||
          payload?.sellerLabelProviderId !== undefined ||
          payload?.serviceCollectionGuid !== undefined
        ) {
          setServiceIdAndSellerLabelProviderId({
            serviceId: payload.serviceId,
            sellerLabelProviderId: payload.sellerLabelProviderId,
          });
          setServiceCollectionGuid(payload.serviceCollectionGuid);
        }

        if (payload?.driverTip) {
          setDriverTip(payload.driverTip);
        } else if (payload?.driverTip === null) {
          setDriverTip({ value: 0, code: sellerCurrencyCode });
        }

        if (payload?.pickup !== undefined) {
          setPickup(payload.pickup);
        }

        if (payload?.customs) {
          setCustoms(payload.customs);
        }

        if (payload?.shipTo !== undefined) {
          setShipTo(payload.shipTo);
        }

        if (payload?.email !== undefined) {
          setEmailAddress(payload.email);
        }

        if (payload?.isReturn !== undefined) {
          setIsReturn(payload.isReturn);
        }

        if (options?.doNotCallForRate) {
          return;
        }

        const completePayload = {
          availableSellerShippingServices,
          availableShippingOptions,
          shipTo,
          email: emailAddress,
          isReturn,
          packages,
          shipFromId,
          shipDate,
          insuranceProvider: insuranceProvider.name,
          options: otherShippingOptions,
          serviceId: serviceIdAndSellerLabelProviderId.serviceId,
          sellerLabelProviderId: serviceIdAndSellerLabelProviderId.sellerLabelProviderId,
          serviceCollectionGuid,
          driverTip,
          pickup,
          customs,
          needsCustoms,
          ...payload,
        };
        const newCswTypeId = updateCSWTypeId(
          payload?.shipFromId || shipFromId,
          null,
          payload?.serviceId || serviceIdAndSellerLabelProviderId?.serviceId,
        );
        if (!shouldUpdateNeedsCustoms) {
          if (isAddressCompleteForRating(completePayload.shipTo) && !shouldSkipRateRequest) {
            setDisableTriggerRate(false);
            completePayload.customs = needsCustoms?.needsCustoms ? customs : null;
            dispatch(Actions.getRateWithPayload(fulfillmentPlanId, completePayload, newCswTypeId));
          } else {
            setDisableTriggerRate(true);
          }
          return;
        }
        updateNeedsCustoms(
          completePayload.shipFromId,
          completePayload.shipTo,
          completePayload.sellerlabelProviderId,
          completePayload.serviceId,
          null,
          false,
        ).then((newNeedsCustoms) => {
          if (!newNeedsCustoms) {
            return;
          }
          const updatedPayload = {
            ...completePayload,
            needsCustoms: newNeedsCustoms.needsCustoms,
            customs: newNeedsCustoms.customs,
            customsReason: newNeedsCustoms.reason,
          };
          setNeedsCustoms(newNeedsCustoms);
          if (isAddressCompleteForRating(completePayload.shipTo) && !shouldSkipRateRequest) {
            setDisableTriggerRate(false);
            dispatch(Actions.getRateWithPayload(fulfillmentPlanId, updatedPayload, newCswTypeId));
          } else {
            setDisableTriggerRate(true);
          }
        });
      },
      [
        dispatch,
        fulfillmentPlanId,
        updateNeedsCustoms,
        availableShippingOptions,
        availableSellerShippingServices,
        shipTo,
        emailAddress,
        isReturn,
        sellerCurrencyCode,
        packages,
        shipFromId,
        shipDate,
        insuranceProvider,
        otherShippingOptions,
        serviceIdAndSellerLabelProviderId,
        serviceCollectionGuid,
        driverTip,
        pickup,
        customs,
        needsCustoms,
        updateCSWTypeId,
      ],
    );

    const triggerRate = useCallback(() => {
      onConfigurationChange({});
    }, [onConfigurationChange]);

    const { onShipToChange, onShipToEmailChange } = useShipToState(fulfillmentPlanId, onConfigurationChange, shipFromId, triggerRate);

    const { onIsReturnChanged } = useIsReturnState(fulfillmentPlanId, onConfigurationChange);

    const cswTypeId = useMemo(() => {
      return updateCSWTypeId(shipFromId, null, serviceIdAndSellerLabelProviderId?.serviceId);
    }, [shipFromId, serviceIdAndSellerLabelProviderId, updateCSWTypeId]);
    return (
      <FulfillmentPlannerContextProvider
        availableDutiesOptions={availableDutiesOptions}
        availableSellerShippingServices={availableSellerShippingServices}
        cswTypeId={cswTypeId}
        customs={customs}
        customsReason={needsCustoms?.reason}
        driverTip={driverTip}
        emailAddress={emailAddress}
        fulfillmentPlanId={fulfillmentPlanId}
        getNewCswTypeId={getNewCswTypeId}
        insuranceProvider={insuranceProvider}
        isReturn={isReturn}
        isTriggerRateDisabled={disableTriggerRate}
        needsCustoms={needsCustoms}
        onAvailableSellerShippingServicesChange={setAvailableSellerShippingServices}
        onConfigurationChange={onConfigurationChange}
        onIsReturnChanged={onIsReturnChanged}
        onShipToChange={onShipToChange}
        onShipToEmailChange={onShipToEmailChange}
        otherShippingOptions={otherShippingOptions}
        packages={packages}
        pickup={pickup}
        serviceCollectionGuid={serviceCollectionGuid}
        serviceIdAndSellerLabelProviderId={serviceIdAndSellerLabelProviderId}
        shipDate={shipDate}
        shipFromId={shipFromId}
        shipTo={shipTo}
        shouldShowDutiesAndTaxes={shouldShowDutiesAndTaxes}
        triggerRate={triggerRate}
      >
        {children}
      </FulfillmentPlannerContextProvider>
    );
  },
);

RateBrowserAdapterForFulfillmentPlannerContext.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)]).isRequired,
  fulfillmentPlanId: PropTypes.oneOf([RATE_BROWSER, CUSTOMERS_CREATE_LABEL]).isRequired,
  initialEmailAddress: PropTypes.string,
  initialShipTo: addressPropType.isRequired,
};
